import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { TodoItemResponse, TodoItemStatus } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useToast } from '@/components/ui/use-toast';

interface CreateItemInput {
  title: string;
  description?: string;
  sectionId?: string | null;
  sortOrder?: number;
  /** When set, create a sub-task under this parent task instead of a top-level item. */
  parentItemId?: string | null;
}

interface UpdateItemInput {
  title?: string;
  description?: string | null;
  status?: TodoItemStatus;
  /** Doer's "finished my part" flag (review-list checkbox), orthogonal to status. */
  done?: boolean;
  sectionId?: string | null;
  sortOrder?: number;
  /** Set/clear the task's workspace project (null clears it). */
  projectId?: string | null;
  /** With status='in_review', route the review to one reviewer (null =
   * broadcast to all list reviewers). */
  reviewAssignedToPrincipalId?: string | null;
  /** Toggle this task's public visibility (owner/admin only). */
  hiddenFromPublic?: boolean;
}

interface ListItemsFilters {
  sectionId?: string;
  status?: TodoItemStatus;
}

interface UseTodoItemsReturn {
  items: TodoItemResponse[];
  loading: boolean;
  refresh: () => void;
  createItem: (input: CreateItemInput) => Promise<TodoItemResponse>;
  updateItem: (vars: { itemId: string; input: UpdateItemInput }) => Promise<TodoItemResponse>;
  deleteItem: (itemId: string) => Promise<void>;
  reorderItems: (order: string[], sectionId?: string | null) => Promise<void>;
  assignItem: (vars: { itemId: string; assigneePrincipalId: string }) => Promise<TodoItemResponse>;
  unassignItem: (itemId: string) => Promise<TodoItemResponse>;
}

export function useTodoItems(
  holderId: string | null,
  listId: string,
  filters?: ListItemsFilters,
): UseTodoItemsReturn {
  const queryClient = useQueryClient();
  const itemsKey = queryKeys.todoLists.items(
    holderId ?? '',
    listId,
    filters as Record<string, unknown> | undefined,
  );
  const { toast } = useToast();

  const onError = () => {
    toast({ title: 'Something went wrong', variant: 'destructive' });
  };

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.todoLists.detail(holderId ?? '', listId),
    });
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: itemsKey,
    queryFn: () => api.listTodoItems(holderId as string, listId, filters),
    enabled: !!holderId && !!listId,
  });

  const createMut = useMutation({
    mutationFn: (input: CreateItemInput) =>
      api.createTodoItem(holderId as string, { listId, ...input }).then((r) => r.item),
    onSuccess: invalidate,
    onError,
  });

  const updateMut = useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: UpdateItemInput }) =>
      api.updateTodoItem(holderId as string, itemId, input).then((r) => r.item),
    onSuccess: invalidate,
    onError,
  });

  const deleteMut = useMutation({
    mutationFn: (itemId: string) => api.deleteTodoItem(holderId as string, itemId),
    onSuccess: invalidate,
    onError,
  });

  const reorderMut = useMutation({
    mutationFn: (vars: { order: string[]; sectionId?: string | null }) =>
      api.reorderTodoItems(holderId as string, listId, vars.order, vars.sectionId),
    onSuccess: invalidate,
    onError,
  });

  const assignMut = useMutation({
    mutationFn: ({
      itemId,
      assigneePrincipalId,
    }: {
      itemId: string;
      assigneePrincipalId: string;
    }) => api.assignTodoItem(holderId as string, itemId, assigneePrincipalId).then((r) => r.item),
    onSuccess: invalidate,
    onError,
  });

  const unassignMut = useMutation({
    mutationFn: (itemId: string) =>
      api.unassignTodoItem(holderId as string, itemId).then((r) => r.item),
    onSuccess: invalidate,
    onError,
  });

  return {
    items: data?.items ?? [],
    loading: isLoading,
    refresh: () => {
      refetch();
    },
    createItem: (input) => createMut.mutateAsync(input),
    updateItem: (vars) => updateMut.mutateAsync(vars),
    deleteItem: (itemId) => deleteMut.mutateAsync(itemId),
    reorderItems: (order: string[], sectionId?: string | null) =>
      reorderMut.mutateAsync({ order, sectionId }),
    assignItem: (vars) => assignMut.mutateAsync(vars),
    unassignItem: (itemId) => unassignMut.mutateAsync(itemId),
  };
}
